<template>
  <div class="tw-flex tw-flex-row tw-space-x-8 tw-w-full">
    <div class="tw-w-10/12">
      <InocuBarChart :series="series" :xaxis="xaxis" :isLoading="isLoading" isMoney>
        <template #title>{{ t('statistics.titles.order_volume') }}</template>
      </InocuBarChart>
    </div>

    <div class="tw-w-2/12 tw-flex tw-flex-col tw-gap-y-4 tw-max-h-72">
      <InocuCard
        :title="t('statistics.titles.total_order_volume')"
        :value="totalOrdersVolume"
        :trendingValue="totalOrdersVolumeComparison"
      >
        <template #icon>
          <totalRequestedOrdersIcon />
        </template>
        <template #value>
          {{ formattedTotalOrdersVolume.value }}
          <span class="tw-text-sm">
            {{ formattedTotalOrdersVolume.unit }}
          </span>
        </template>
      </InocuCard>

      <InocuCard
        :title="t('statistics.titles.total_order_volume_approved')"
        :value="totalApprovedOrdersVolume"
        :trendingValue="totalApprovedOrdersVolumeComparison"
        dark
      >
        <template #icon>
          <approvedOrdersIcon />
        </template>
        <template #value>
          {{ formattedApprovedOrdersVolume.value }}
          <span class="tw-text-sm">{{ formattedApprovedOrdersVolume.unit }}</span>
        </template>
      </InocuCard>
    </div>
  </div>
</template>

<script setup lang="ts">
import totalRequestedOrdersIcon from '../icons/totalRequestedOrdersIcon.svg';
import approvedOrdersIcon from '../icons/approvedOrdersIcon.svg';
import { ref, onMounted, watch, type Ref, computed } from 'vue';
import InocuBarChart from '@/components/shared/charts/InocuBarChart.vue';
import { useI18n } from 'vue-i18n';
import { useDashboardStore } from '@/stores/dashboard.store';
import { useFormatValue } from '@/_helpers/composables/formatting.composable';
import InocuCard from '@/components/shared/cards/InocuCard.vue';
const { formatCurrency } = useFormatValue();
const { getOrdersVolume } = useDashboardStore();

const isLoading = ref(true);
const { t } = useI18n();

const props = defineProps<{
  startDate: string;
  endDate: string;
  customerId: number;
  includeRecurring: boolean;
}>();

const series: Ref<{ name: string; data: number[] }[]> = ref([
  {
    name: t('statistics.titles.total_order_volume_approved'),
    data: [],
  },
  {
    name: t('statistics.titles.total_order_volume'),
    data: [],
  },
]);
const xaxis: Ref<string[]> = ref([]);
const totalOrdersVolume = ref(0);
const totalApprovedOrdersVolume = ref(0);
const totalOrdersVolumeComparison = ref(0);
const totalApprovedOrdersVolumeComparison = ref(0);

const formattedTotalOrdersVolume: Ref<any> = computed(() => {
  return formatCurrency(totalOrdersVolume.value, true);
});

const formattedApprovedOrdersVolume: Ref<any> = computed(() => {
  return formatCurrency(totalApprovedOrdersVolume.value, true);
});

const fetchData = async () => {
  isLoading.value = true;
  try {
    const data = await getOrdersVolume(
      props.startDate,
      props.endDate,
      props.customerId,
      props.includeRecurring
    );
    series.value[0].data = data.approved_orders_volume;
    series.value[1].data = data.all_orders_volume;
    xaxis.value = data.dates;
    totalOrdersVolume.value = data.total_orders_volume;
    totalApprovedOrdersVolume.value = data.total_approved_orders_volume;
    totalOrdersVolumeComparison.value = data.total_orders_volume_comparison;
    totalApprovedOrdersVolumeComparison.value = data.total_approved_orders_volume_comparison;
  } catch (error) {
    console.error('Error fetching orders volume:', error);
  } finally {
    isLoading.value = false;
  }
};
onMounted(fetchData);

watch(() => props, fetchData, { deep: true });
</script>
